import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Image as ImageIcon } from "lucide-react";
import { adminClient } from "./api";
import ImageUploader from "./ImageUploader";

const EMPTY = { image_url: "", caption: "", sort_order: 0, is_active: true };

export default function HeroImagesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminClient.get("/admin/hero-images");
      setItems(data.items || []);
    } catch { toast.error("Could not load hero images"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setOpen(true); };

  const notify = () => {
    window.dispatchEvent(new CustomEvent("hp:hero-changed"));
    try { localStorage.setItem("hp:hero-changed", String(Date.now())); } catch {}
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.image_url) return toast.error("Upload an image first");
    const payload = {
      image_url: form.image_url,
      caption: form.caption || "",
      sort_order: parseInt(form.sort_order, 10) || 0,
      is_active: !!form.is_active,
    };
    try {
      if (editing) await adminClient.put(`/admin/hero-images/${editing.id}`, payload);
      else await adminClient.post("/admin/hero-images", payload);
      toast.success("Saved · Live on site now", { icon: "🟢" });
      notify();
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm("Remove this hero image?")) return;
    try {
      await adminClient.delete(`/admin/hero-images/${item.id}`);
      toast.success("Removed · Live on site now", { icon: "🟢" });
      notify();
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggle = async (item) => {
    try {
      await adminClient.put(`/admin/hero-images/${item.id}`, { ...item, is_active: !item.is_active });
      notify();
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="hero-images-admin-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-2xl text-[#0C2E2B]">Hero Images</h1>
          <p className="text-sm text-zinc-500">Photos that rotate in the hero on the home page. They auto-rotate every 5 seconds.</p>
        </div>
        <Button data-testid="hero-add-btn" onClick={openCreate} className="btn-kinetic rounded-md text-xs">
          <Plus className="w-4 h-4 mr-1" /> Add Photo
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading...</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center">
          <ImageIcon className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="font-bold">No custom hero photos yet</p>
          <p className="text-xs text-zinc-500 mt-1">The site shows a default dump truck until you add your own.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => (
            <Card key={it.id} data-testid={`hero-card-${it.id}`} className="overflow-hidden border-2 border-zinc-200">
              <div className="relative">
                <img src={it.image_url} alt={it.caption || "hero"} className="w-full h-40 object-cover" />
                {!it.is_active && <div className="absolute inset-0 bg-black/60 grid place-items-center text-white font-bold">HIDDEN</div>}
              </div>
              <div className="p-4 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-sm text-[#0C2E2B] truncate">{it.caption || "(no caption)"}</div>
                  <div className="text-[10px] text-zinc-500">Sort {it.sort_order}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch data-testid={`hero-toggle-${it.id}`} checked={it.is_active} onCheckedChange={() => toggle(it)} />
                  <Button size="sm" variant="outline" className="h-7" data-testid={`hero-edit-${it.id}`} onClick={() => openEdit(it)}>Edit</Button>
                  <Button size="sm" variant="outline" className="h-7 text-red-600" data-testid={`hero-delete-${it.id}`} onClick={() => remove(it)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit hero image" : "New hero image"}</DialogTitle>
            <DialogDescription>Photos rotate in the homepage hero. Lower sort order shows first.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="hero-form">
            <ImageUploader value={form.image_url} onChange={(u) => setForm({ ...form, image_url: u })} testId="hero-image-uploader" />
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Caption (optional)</Label>
              <Input data-testid="hero-form-caption-input" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="e.g. Same-day dispatch" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3 items-center">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Sort order</Label>
                <Input data-testid="hero-form-sort-input" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch data-testid="hero-form-active-switch" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <span className="text-sm">{form.is_active ? "Live on site" : "Hidden"}</span>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="hero-form-submit-btn" type="submit" className="btn-kinetic rounded-md flex-1">
                {editing ? "Save changes" : "Add photo"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
